import { pipe, Option, Array as A } from 'effect';
import moment from 'moment';
import { z } from 'zod';

import { TrailListQuerySchema } from '@/domain/events/schemas';

type TrailListQuery = z.infer<typeof TrailListQuerySchema>;

export const TRAIL_PAGE_SIZE = 10;

export interface TrailPanelFilters {
  users: ReadonlyArray<string>;
  types: ReadonlyArray<string>;
  texts: ReadonlyArray<string>;
  freeText: string;
}

export interface TrailFilters extends TrailPanelFilters {
  from: Date | null;
  to: Date | null;
}

export const TRAIL_PANEL_FILTERS_INITIAL: TrailPanelFilters = {
  users: [],
  types: [],
  texts: [],
  freeText: '',
};

export const TRAIL_FILTERS_INITIAL: TrailFilters = {
  ...TRAIL_PANEL_FILTERS_INITIAL,
  from: null,
  to: null,
};

const bound = (date: Date | null): string | undefined =>
  pipe(
    Option.fromNullable(date),
    Option.map((d) => moment(d).format('YYYY-MM-DD')),
    Option.getOrUndefined,
  );

const list = (values: ReadonlyArray<string>): string[] | undefined =>
  pipe(
    values,
    A.filter((v) => v.length > 0),
    (kept) => (A.isNonEmptyArray(kept) ? kept : undefined),
  );

export const buildTrailFilterParams = (filters: TrailFilters) => ({
  users: list(filters.users),
  types: list(filters.types),
  texts: list([...filters.texts, filters.freeText]),
  timestamp_from: bound(filters.from),
  timestamp_to: bound(filters.to),
});

// Only Pay is scoped today; the events service calls the scope `payment_id`.
export const buildTrailScopeParams = (entityId?: string) => (entityId ? { payment_id: entityId } : {});

export const buildTrailListQuery = (filters: TrailFilters, page: string, entityId?: string): TrailListQuery => ({
  ...(page.length > 0 ? { page } : {}),
  limit: TRAIL_PAGE_SIZE,
  ...buildTrailScopeParams(entityId),
  ...buildTrailFilterParams(filters),
});
